"use client";

import { useMemo, useEffect } from "react";

import { useUserLocation } from "@/context/user-location.context";
import { filterFormFieldsByLocation } from "@marketing-sites/utils/filter-form-fields-by-location";

import { Button, Form as FormPrimitive } from "@marketing-sites/ui/primitives";

import { useMarketoForm } from "@marketing-sites/marketo/use-marketo-form";
import { renderFields } from "@marketing-sites/marketo/render-fields";
import { toFieldProps } from "@marketing-sites/marketo/field-props";
import { getFormFields } from "@/lib/marketo/get-form-fields";
import { FORM_CONFIGS } from "@/constants/marketo/form-ids";
import ArrowSmall from "@/icons/arrow-small.svg";

import { submit } from "./submit.action";
import { ButtonLink } from "@/app/[locale]/(legacy)/customer-transformation-report/components/ButtonLink";
import { rememberSubmittedLeadValues } from "@/lib/marketo/remember-lead-values";

const pdfUrl =
  "https://cdn.sanity.io/files/4bgok82s/monorepo-static-assets/fa438e9d1b1e94d976f115a479d5d5333d3c3316.pdf";

const { id, fallbackFields } = FORM_CONFIGS.CONTENT_REGISTRATION;
const formFields = getFormFields(id, fallbackFields);

export const Form = () => {
  const { isOptInCountry } = useUserLocation();

  const filteredFields = useMemo(
    () =>
      toFieldProps(filterFormFieldsByLocation(formFields, isOptInCountry), {
        personalEmailMetadata: {
          analyticsId:
            "form_customer_transformation_report_personal_email_detected",
        },
      }),
    [isOptInCountry],
  );

  const { status, formAction, pending, handleFormSubmit } = useMarketoForm({
    action: submit,
    formId: id,
    analyticsLocation: "Form",
    submitMetadata: {
      analyticsId: "form_customer_transformation_report_submit",
    },
    onSuccess: (result) => {
      rememberSubmittedLeadValues(result.formValues);
    },
  });

  useEffect(() => {
    if (status?.success) {
      window.open(pdfUrl, "_blank");
      window.location.href = `${window.location.href}#report-form`;
    }
  }, [status?.success]);

  if (status?.success)
    return (
      <div
        data-testid="guarantee-form-success"
        className="aspect-341/283 md:aspect-719/224 xl:aspect-611/400 flex md:p-1 lg:p-4"
      >
        <div className="flex flex-1 flex-col justify-between">
          <div>
            <h3 className="text-report-body-large-mobile md:text-report-body-large mb-4 md:mb-8">
              Thank You
            </h3>

            <h3 className="text-report-body-large-mobile md:text-report-body-large">
              You now can access the 2026 Customer Service Transformation
              Report. Download it at the link below.
            </h3>
          </div>

          <ButtonLink label="Open the report" href={pdfUrl} external={true} />
        </div>
      </div>
    );

  return (
    // Interject here with new brand for the Form stuff
    // Note - we use a bunch of custom tailwind shit here which is gross. But it's way less gross than using the legacy Form molecule
    <div className="brand-i2y4">
      <FormPrimitive.Root
        action={formAction}
        onSubmit={handleFormSubmit}
        className="theme-navy flex w-full flex-col text-left"
        data-analytics-region="Form"
        data-analytics-id="form_customer_transformation_report"
      >
        <div className="*:md:nth-[1]:col-span-1 *:md:nth-[2]:col-span-1 *:md:nth-[3]:col-span-1 *:md:nth-[4]:col-span-1 [&_label]:text-xs! [&_label]:font-mono! [&_.checkbox-label]:normal-case! [&_.checkbox-label]:font-sans! [&_.checkbox-label]:text-sm! [&_.checkbox-label]:tracking-normal! [&_.input]:rounded-none! [&_.input]:h-12! grid grid-cols-2 gap-4 *:col-span-full [&_.checkbox-label]:w-10/12 [&_label]:uppercase [&_label]:tracking-wide">
          {renderFields(filteredFields)}
        </div>

        <Button
          analyticsId="btn_customer_transformation_report_form_submit"
          type="submit"
          disabled={pending}
          className="text-report-body-small-mobile before:bg-report-grey-400/70 relative isolate mt-6 flex h-12 w-full cursor-pointer items-center justify-between whitespace-nowrap rounded-none border-none bg-black px-6 py-4 text-left font-mono text-sm font-medium uppercase tracking-[1.5px] text-white before:absolute before:inset-0 before:-z-20 before:origin-bottom before:scale-y-0 before:transition before:ease-in-out hover:bg-black/90 hover:before:scale-y-100 disabled:cursor-not-allowed disabled:opacity-50 xl:mt-24"
        >
          {pending ? "Please wait..." : "Download The Report"}
          <ArrowSmall />
        </Button>
        {status?.error && (
          <p className="block w-full border border-red-700 p-1.5 font-sans text-red-700">
            {status.error}
          </p>
        )}
      </FormPrimitive.Root>
    </div>
  );
};
